/**
 * useMentalityChat — manages the user/bot chat turn list + the LLM call.
 *
 * Chat persistence lives in `getMentalityChatHistory()` + `postMentalityChat`.
 * Replay hydrates the turn list when the overlay opens; a second effect
 * resolves rule refs against the fresh context so cards render in replay
 * the same way they do live.
 */

import { useCallback, useEffect, useState } from 'react';
import {
    postMentalityChat, getMentalityChatHistory, type MentalityContext, type ModuleKey,
} from '../../../mentorshipService';
import { resolveRuleRefs, type MatchCard } from './adapters';

export type ChatEntry = {
    id: number;
    from: 'user' | 'bot';
    text: string;
    cards?: MatchCard[];
    /** Rule refs carried from chat history; resolved into `cards`
     *  once the fresh context is available. */
    refs?: { id: string; kind: string }[];
    at: number;
};

type UseMentalityChatArgs = {
    open: boolean;
    context: MentalityContext | null;
    module: ModuleKey | '';
    docName: string;
    docText: string;
    cancelRef: React.MutableRefObject<boolean>;
};

export function useMentalityChat({
    open, context, module, docName, docText, cancelRef,
}: UseMentalityChatArgs) {
    const [chat, setChat] = useState<ChatEntry[]>([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        void getMentalityChatHistory()
            .then((turns) => {
                if (cancelRef.current) return;
                if (turns.length === 0) { setChat([]); return; }
                setChat(turns.map((t, i) => ({
                    id: i,
                    from: t.role === 'user' ? 'user' : 'bot',
                    text: t.text,
                    cards: [],
                    at: Date.parse(t.created_at),
                    refs: t.rule_refs,
                })));
            })
            .catch(() => { /* empty history is fine */ });
    }, [open, cancelRef]);

    useEffect(() => {
        if (!context) return;
        setChat((prev) => prev.map((entry) => {
            if (entry.from !== 'bot' || !entry.refs || entry.cards?.length) return entry;
            return { ...entry, cards: resolveRuleRefs(entry.refs, context) };
        }));
    }, [context]);

    const send = useCallback(async (query: string) => {
        const q = query.trim();
        if (!q || !context || sending) return;
        const userEntry: ChatEntry = { id: Date.now(), from: 'user', text: q, at: Date.now() };
        setChat((prev) => [...prev, userEntry]);
        setSending(true);
        try {
            const resp = await postMentalityChat({
                query: q,
                module: (module || undefined) as ModuleKey | undefined,
                documentName: docName.trim() || undefined,
                documentText: docText.trim() || undefined,
            });
            const resolved = resolveRuleRefs(resp.rules, context);
            const botEntry: ChatEntry = {
                id: Date.now() + 1,
                from: 'bot',
                text: resp.answer + (resp.followup ? `\n\n${resp.followup}` : ''),
                cards: resolved,
                at: Date.now(),
            };
            setChat((prev) => [...prev, botEntry]);
        } catch (err) {
            console.error('[MentalityOverlay] chat failed', err);
            setChat((prev) => [...prev, {
                id: Date.now() + 1,
                from: 'bot',
                text: 'The assistant is temporarily unavailable. Please try again shortly.',
                at: Date.now(),
            }]);
        } finally {
            setSending(false);
        }
    }, [context, docName, docText, module, sending]);

    return { chat, sending, send };
}
